interface IGetCroppedImageFileParams {
  /** The source canvas to draw from. */
  previewCanvas: HTMLCanvasElement;
  /** The full file name of the original image, including extension (e.g., 'my-image.png'). */
  fileName: string;
  /**
   * The desired width of the output image.
   * If not provided, the natural width of the previewCanvas will be used.
   */
  targetWidth?: number;
  /**
   * The quality setting (0 to 1).
   * This is only used for 'image/jpeg'.
   */
  quality?: number;
}

export const getCroppedImageFile = async ({
  previewCanvas,
  fileName,
  targetWidth,
  quality = 0.85,
}: IGetCroppedImageFileParams): Promise<File | null> => {
  if (!previewCanvas) {
    throw new Error('Preview canvas is not available.');
  }

  // Determine the output format based on the original file's extension.
  const isPng = fileName.toLowerCase().endsWith('.png');
  const type = isPng ? 'image/png' : 'image/jpeg';

  // Calculate width and height, preserving aspect ratio
  const naturalAspectRatio = previewCanvas.width / previewCanvas.height;
  const finalWidth = targetWidth ?? previewCanvas.width;
  const finalHeight = finalWidth / naturalAspectRatio;

  const finalCanvas = document.createElement('canvas');
  finalCanvas.width = finalWidth;
  finalCanvas.height = finalHeight;

  // Explicitly get context with alpha support for PNG transparency
  const ctx = finalCanvas.getContext('2d', { alpha: true });
  if (!ctx) {
    throw new Error('Could not get 2D drawing context');
  }

  //  If the output is a JPEG, fill the background with white.
  //  This prevents transparent areas from turning black.
  //  For PNGs, this is skipped, preserving transparency.
  if (type === 'image/jpeg') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, finalWidth, finalHeight);
  }

  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    previewCanvas,
    0,
    0,
    previewCanvas.width,
    previewCanvas.height,
    0,
    0,
    finalWidth,
    finalHeight
  );

  return new Promise((resolve, reject) => {
    finalCanvas.toBlob(
      blob => {
        if (!blob) {
          reject(new Error('Failed to create image blob.'));
          return;
        }
        // 3. Create the file using the original name and the determined MIME type.
        resolve(new File([blob], fileName, { type }));
      },
      type, // Pass the determined file type
      quality // Pass the quality (ignored for PNGs)
    );
  });
};
